import * as Model from "@effect/sql/Model";
import { Schema } from "effect";
import { IntakeSource } from "./Intake.ts";

/**
 * Lead lifecycle status — the CSR sales funnel head.
 * @since 1.0.0
 */
export const LeadStatus = Schema.Literal(
  "new",
  "contacted",
  "qualified",
  "converted",
  "dropped",
);
export type LeadStatus = Schema.Schema.Type<typeof LeadStatus>;

/**
 * How a lead was acquired. Mirrors {@link IntakeSource} (same values, same
 * underlying `intake_source` pgEnum) so a lead's channel carries straight
 * through to the intake it becomes.
 *
 * @since 1.0.0
 */
export const LeadSource = IntakeSource;
export type LeadSource = Schema.Schema.Type<typeof LeadSource>;

/**
 * Lead model — the head of the CSR sales funnel.
 *
 * A lead is a person we have not run intake on yet (no customer, no intake, no
 * pricing). `personId` points at the existing `people` row (a lead is a person
 * with `clerkId` NULL — not signed up yet). Converting a lead runs the intake
 * and stamps `convertedIntakeId` + `status = 'converted'`.
 *
 * Kept SEPARATE from the marketing `Waitlist` model — the sales funnel and the
 * signup list are not merged.
 *
 * @since 1.0.0
 */
export class Lead extends Model.Class<Lead>("Lead")({
  id: Model.Generated(Schema.String),
  personId: Schema.String,
  source: LeadSource,
  sourceDetail: Schema.NullOr(Schema.String),
  status: Schema.optionalWith(LeadStatus, { default: () => "new" as const }),
  assignedToPersonId: Schema.NullOr(Schema.String),
  notes: Schema.NullOr(Schema.String),
  convertedIntakeId: Schema.NullOr(Schema.String),
  createdAt: Model.DateTimeInsertFromDate,
  updatedAt: Model.DateTimeUpdateFromDate,
}) {}
